import type { DropdownOption } from 'components/DropdownListWithOptions';
import { Visibility, VisibilityOff } from 'components/Icons';
import { EMPTY_ARRAY, EMPTY_OBJECT } from 'constants/defaults';
import { HAS_SEEN_SUGGESTED_HOUR } from 'constants/hints/onboarding';
import { HIDE_TASK_SIDEBAR, SHOW_MEMORIES_ON_HOURS } from 'constants/user';
import type { GroupHoursByDatesType, HourTotals } from 'factories/calendar';
import { calculateHourTotals, groupHoursByDates } from 'factories/calendar';
import type { TeamWithUsers } from 'factories/teams';
import { isEqual, uniq } from 'lodash-es';
import React from 'react';
import type { BatchHourModalMode, FilterSpan } from 'redux/modules/calendar.types';
import { isDayLockedForUser } from 'redux/modules/dayProperties';
import type { DayProperties } from 'redux/modules/dayProperties.schema';
import { getDayProperties } from 'redux/modules/dayProperties.selectors';
import { getUserSelectableHourStates } from 'redux/modules/hourStates';
import { xhrStatusUIState } from 'redux/reusable/xhrStatus';
import {
  getAccountCurrency,
  getCurrentAccount,
  getHasBillingFeature,
  getHasHourStatesFeature,
  getHasReachedUserLimit,
  getStartOfWeek,
  getTaskAddOnInTrial,
} from 'redux/selectors/accounts';
import { getHints } from 'redux/selectors/hints';
import {
  getHours,
  getHoursAsCards,
  getHoursXhrStatus,
  getHourTimerStatus,
} from 'redux/selectors/hours';
import type { GetHourDraftStatus } from 'redux/selectors/hours.draft';
import { getHasHourDraft, getHourDraftStatus } from 'redux/selectors/hours.draft';
import { getIsRouteCalendar } from 'redux/selectors/routing';
import { getCanSeeTasks } from 'redux/selectors/subscriptions';
import {
  getSuggestionAsCardsByDay,
  getSuggestionResult,
  getSuggestions,
} from 'redux/selectors/suggestions';
import { getTeamsAsDropdownOptions, getTeamsWithUsers } from 'redux/selectors/teams';
import {
  getHasEnabledTic,
  getIsUserInExperimentalFeatureGroup,
  getUser,
  getUserId,
  getUserPermissions,
  getUserProperties,
  getUserPropertiesXhrStatus,
} from 'redux/selectors/user';
import { getNonDeletedUsers } from 'redux/selectors/users';
import type { IState } from 'redux/state';
import { createSelector, lruMemoize } from 'reselect';
import type { HourAnalyticsSaveContext } from 'types/analytics';
import type { HourAsCard } from 'types/hour';
import type { Cost, Duration } from 'types/units';
import type { User } from 'types/user';
import type { SummarizeCostAndDurationOutput } from 'utils/hours';
import { summarizeCostAndDuration } from 'utils/hours';
import { getIsViewingTimesheet } from '../../domains/ai/timesheet_proposals/redux/tic.timesheetProposals.slice';
import { getHasConnectedMemorySource } from './apps';
import { getDates, getDerivedDatesInfo, getFilter } from './calendar.filter';
import { getProposedForecastsByDay } from './calendar.proposals';
import { getReportSelection } from './superreports';
import { getMemoryViewmode, type MemoryViewmode } from './timeline';

const readSelection = (state: IState) => state.calendar.selection;
const readBatchHourModalMode = (state: IState) => state.calendar.batchHourModalMode;

export const readShowMemoryInbox = (state: IState) => state.calendar.showMemoryInbox;

/**
 * Show Memory Inbox show (forced false if we have Hour Draft)
 */
export const getShowMemoryInbox = createSelector(
  [getHourDraftStatus, readShowMemoryInbox, getIsRouteCalendar],
  (draftStatus, showMemory, isRouteCalendar): boolean =>
    showMemory && !draftStatus.exists && isRouteCalendar
);

export const getListMemoriesOnHours = createSelector(
  [getUser],
  (user): boolean => user.properties[SHOW_MEMORIES_ON_HOURS] !== false
);

export const getSelection = readSelection;

// This can only be null before the user is authenticated
export const getCalendarUserId = createSelector(
  [getFilter],
  (filter): number => filter.calendarUserId
);

export const getCalendarUser = createSelector(
  [getCalendarUserId, getNonDeletedUsers, getUser],
  (userId, users, authenticatedUser): User =>
    users.find(({ id }) => id === userId) || authenticatedUser
);

// todo: Revisit this approach when doing company view
export const getHoursAsCardsForCalendarUserId = createSelector(
  [getCalendarUserId, getHoursAsCards],
  (userId, hours): Array<HourAsCard> => hours.filter(hour => hour.user_id === userId)
);

type EntryIdDayMap = {
  [date: string]: Array<number>;
};

/**
 * Returns entry_ids for suggestions, key'ed by date
 */
export const getSuggestionEntryIdDayMap = createSelector(
  [getSuggestions, getFilter],
  (syggestions, filter): EntryIdDayMap => {
    // Only authenticated user viewing themselves should see this
    if (!filter.isActingAsSelf) {
      return EMPTY_OBJECT;
    }

    return syggestions.reduce<EntryIdDayMap>((prev, suggestion) => {
      const existingEntryIds = prev[suggestion.date] || EMPTY_ARRAY;
      return {
        ...prev,
        // Return only unique entry.id's for each day, as we're doing
        // cheap .length comparisons in getDaysWithEntrySummary
        [suggestion.date]: uniq([...suggestion.suggested_entry_ids, ...existingEntryIds]),
      };
    }, {});
  }
);

/**
 * Returns entry_ids for hours, key'ed by date
 */
export const getHourEntryIdDayMap = createSelector(
  [getHoursAsCardsForCalendarUserId, getFilter],
  (hours, filter): EntryIdDayMap => {
    // Only authenticated user viewing themselves should see this
    if (!filter.isActingAsSelf) {
      return EMPTY_OBJECT;
    }

    return hours.reduce<EntryIdDayMap>((prev, hour) => {
      const existingEntryIds = prev[hour.day] || EMPTY_ARRAY;
      return {
        ...prev,
        // Return only unique entry.id's for each day, as we're doing
        // cheap .length comparisons in getDaysWithEntrySummary
        [hour.day]: uniq([...hour.entryIds, ...existingEntryIds]),
      };
    }, {});
  }
);

interface CalendarCounts {
  days: number;
  columns: number;
}

export const getCalendarCounts = createSelector([getDates], (dates): CalendarCounts => {
  return {
    days: dates.length,
    columns: dates.length > 7 ? 7 : dates.length,
  };
});

export const getDayHeaderKeys = createSelector(
  [getDates, getCalendarCounts],
  (dates, calendarCounts): Array<string> => {
    return dates.slice(0, calendarCounts.columns);
  }
);

export const getDaysWithHours = createSelector(
  [getAccountCurrency, getDates, getFilter, getHoursAsCardsForCalendarUserId],
  (currency, dates, filter, hours): GroupHoursByDatesType => {
    return groupHoursByDates({ dates, filter, hours, currency });
  }
);

const getDayPropertiesForDates = createSelector(
  [getDayProperties, getDates],
  (dayProperties, dates) => {
    return dates.reduce(
      (dateMap, date) => {
        dateMap[date] = dayProperties[date] || EMPTY_ARRAY;
        return dateMap;
      },
      {} as Record<string, Array<DayProperties>>
    );
  }
);

const getPartialFilterForTotals = createSelector(getFilter, filter => ({
  date: filter.date,
  span: filter.span,
}));

export const getCalculatedHourTotals = createSelector(
  [getHoursAsCards, getPartialFilterForTotals],
  (hours, filter) => calculateHourTotals(hours, filter)
);

export const getCostDurationOfHoursExceptTimer = createSelector(
  [getHoursAsCards, getFilter],
  (hours, filter) => {
    return summarizeCostAndDuration(hours, { filter, ignoreRunningTimer: true });
  }
);

export const getHintsForCalendar = createSelector(
  [getHints, getSuggestionResult, getHourDraftStatus, readShowMemoryInbox],
  (hints, suggestionsResult, draftStatus, showMemoryInbox) => {
    // Don't Show if the editor is open.
    if (draftStatus && draftStatus.exists) {
      return null;
    }
    if (showMemoryInbox) {
      return null;
    }

    return {
      DayComponent: {
        targetSuggestionId:
          hints[HAS_SEEN_SUGGESTED_HOUR] && suggestionsResult[0] ? suggestionsResult[0] : null,
        showHint: hints[HAS_SEEN_SUGGESTED_HOUR],
      },
    };
  }
);

export interface CalendarUIProps {
  date: string;
  isActingAsSelf: boolean;
  isSingleDay: boolean;
  showMemoriesOnHours: boolean;
  draftStatus: GetHourDraftStatus;
  calendarCounts: CalendarCounts;
  sizeBasedOnDuration: boolean;
  showMemoryInbox: boolean;
  dayOptions: Array<DropdownOption>;
  hasMultipleCalendarDayRows: boolean;
  showInlineDayHeading: boolean;
  useInlineHourEditor?: boolean;
  showUserSelection?: boolean;
  showDebugInfo: boolean;
  isEditingHourInModal: boolean;
  isForecastFeatureEnabled: boolean;
  isBatchCreatePlannedEntriesFeatureEnabled: boolean;
  isDayLockingFeatureEnabled: boolean;
  showForecastSidebar: boolean;
  multiUserMode: boolean;
  hasEnabledTic: boolean;
  memoryViewmode: MemoryViewmode;
}

export type GetCalendarProps = ReturnType<typeof getCalendarProps>;

const getTasksMeta = createSelector(
  [getCanSeeTasks, getTaskAddOnInTrial],
  (canSeeTasks, taskAddOnInTrial) => {
    const accountIsTrialingTasks = taskAddOnInTrial != null;
    return {
      canSeeTasks,
      accountIsTrialingTasks,
    };
  }
);

const getDayOptions = createSelector(
  [getDaysWithHours, getUserProperties],
  (days, userProperties) => {
    const datesWithEntries = Object.entries(days).reduce<Array<string>>((p, [date, v]) => {
      if (v.hours.length) {
        p.push(date);
      }
      return p;
    }, []);

    const options: Array<DropdownOption<Array<string>>> = [
      {
        action: 'clone',
        name: 'Copy entries',
        faIcon: 'copy',
        meta: datesWithEntries,
      },
      {
        action: 'move',
        name: 'Move entries',
        faIcon: 'arrow-right',
        meta: datesWithEntries,
      },
    ];

    if (Object.keys(days).length === 1 && !userProperties.has_enabled_tic) {
      options.unshift({
        action: 'toggleShowMemories',
        name: `${userProperties.show_memories_on_hours ? 'Hide' : 'Show'} memories`,
        icon: userProperties.show_memories_on_hours ? <VisibilityOff /> : <Visibility />,
      });
    }

    return options.filter(o => !!o);
  },
  { memoizeOptions: { resultEqualityCheck: isEqual } }
);

const getCalendarUi = createSelector(
  [
    getFilter,
    getDates,
    getListMemoriesOnHours,
    getCalendarCounts,
    getHourDraftStatus,
    getShowMemoryInbox,
    getIsUserInExperimentalFeatureGroup,
    getCurrentAccount,
    getUserProperties,
    getDayOptions,
    getTasksMeta,
    getMemoryViewmode,
  ],
  (
    filter,
    dates,
    showMemoriesOnHoursSetting,
    calendarCounts,
    draftStatus,
    showMemoryInbox,
    showDebugInfo,
    account,
    userProperties,
    dayOptions,
    { canSeeTasks, accountIsTrialingTasks },
    memoryViewmode
  ) => {
    const alwaysHideTasksSidebar = !account.features.tasks && !accountIsTrialingTasks;
    const isTimespanOverAWeek = dates.length > 7;
    const isSingleDay = dates.length === 1;
    const hasEnabledTic = !!userProperties.has_enabled_tic;
    const showMemoriesOnHours = !hasEnabledTic && showMemoriesOnHoursSetting && isSingleDay;
    const useInlineHourEditor = isSingleDay;
    const isEditingHourInModal = !useInlineHourEditor && draftStatus.exists;
    const ui: CalendarUIProps = {
      date: filter.date,
      isActingAsSelf: filter.isActingAsSelf,
      showUserSelection: !filter.userIds.length,
      isSingleDay,
      showMemoriesOnHours,
      useInlineHourEditor,
      draftStatus,
      calendarCounts,
      sizeBasedOnDuration: filter.span !== 'month' && !showMemoriesOnHours && !filter.multiUserMode,
      showInlineDayHeading: isTimespanOverAWeek,
      showMemoryInbox,
      isEditingHourInModal,
      hasMultipleCalendarDayRows: isTimespanOverAWeek,
      showDebugInfo,
      isForecastFeatureEnabled: !isSingleDay && !!account.features.tasks,
      isBatchCreatePlannedEntriesFeatureEnabled: !!account.features.planned_entries,
      isDayLockingFeatureEnabled: !!account.features.day_locking,
      showForecastSidebar: alwaysHideTasksSidebar
        ? false
        : canSeeTasks && !isSingleDay && !userProperties[HIDE_TASK_SIDEBAR],
      dayOptions,
      multiUserMode: filter.multiUserMode,
      hasEnabledTic,
      memoryViewmode,
    };
    return ui;
  },
  { memoizeOptions: { resultEqualityCheck: isEqual } }
);

export interface DaySelectionState {
  disabled: boolean;
  active: boolean;
  hasSelectedHours: boolean;
  hasUnselectedHours: boolean;
}

const getSelectionStateByDay = createSelector(
  [getReportSelection, getDaysWithHours],
  (selection, hoursByDay) => {
    const selectionStateByDay: Record<string, DaySelectionState> = {};
    const active = selection.hourIds.length > 0;
    Object.keys(hoursByDay).forEach(date => {
      const day = hoursByDay[date];
      const hasSelectedHours = day.hours.some(hour => selection.hourIds.includes(hour.id));
      const unlockedHours = day.hours.filter(hour => !hour.isLocked);
      const numUnselectedHours = unlockedHours.filter(
        hour => !selection.hourIds.includes(hour.id)
      ).length;
      selectionStateByDay[date] = {
        active,
        hasSelectedHours: numUnselectedHours === 0 || hasSelectedHours,
        hasUnselectedHours: numUnselectedHours > 0,
        disabled: unlockedHours.length === 0,
      };
    });
    return selectionStateByDay;
  }
);

export const getCalendarProps = createSelector(
  [
    getDates,
    getDayHeaderKeys,
    getDaysWithHours,
    getSuggestionAsCardsByDay,
    getDayPropertiesForDates,
    getCalendarUserId,
    getHintsForCalendar,
    getCalendarUi,
    getProposedForecastsByDay,
    getUserPermissions,
    getUserId,
    getCalculatedHourTotals,
    getSelectionStateByDay,
  ],
  (
    dates,
    dayHeaderKeys,
    hoursByDay,
    suggestionsByDay,
    dayProperties,
    calendarUserId,
    hints,
    ui,
    proposedForecastsByDay,
    permissions,
    currentUserId,
    hourTotals,
    selectionStateByDay
  ) => {
    return {
      dates,
      dayHeaderKeys,
      hoursByDay,
      dayProperties,
      hourTotals,
      suggestionsByDay,
      calendarUserId,
      hints,
      ui,
      proposedForecastsByDay,
      permissions,
      currentUserId,
      selectionStateByDay,
    };
  }
);

/**
 * !!! getMultiUserCalendarProps is a direct copy of getCalendarProps FOR NOW:
 * Todo:
 * - Reduce this selector to the bare minimum
 * - Common stuff in GetCalendarProps GetMultiUserCalendarProps gets it's own selector
 */

type GetMultiUsersDaysWithHours = Array<{
  key: number;
  user: {
    id: number;
    name: string;
    avatar: string | null;
    friendlyName: string;
  };
  hoursByDay: GroupHoursByDatesType;
  hoursTotals: HourTotals;
  ui: {
    calendarHeightPx: number;
  };
  cacheSignature: any;
}>;

export const getMultiUsersDaysWithHours = createSelector(
  [getNonDeletedUsers, getDates, getFilter, getHoursAsCards, getAccountCurrency],
  (users, dates, filter, allHours, currency): GetMultiUsersDaysWithHours => {
    return users
      .filter(user => filter.userIds.includes(user.id))
      .map(user => {
        const hours = allHours.filter(hour => hour.user_id === user.id);
        const hoursTotals = calculateHourTotals(hours, filter);
        const hoursByDay = groupHoursByDates({
          currency,
          dates,
          filter,
          hours,
          sizeBasedOnDuration: true,
        });

        const hourCardHeightPx = dates.reduce((prev, day) => {
          const { totalHourCardHeightPx } = hoursByDay[day].ui;
          const totalMarginsPx = hoursByDay[day].hours.length * 16; // Must mirror $hourCardSpaceBetween css variable
          const totalHoursHeightPx = totalHourCardHeightPx + totalMarginsPx - 20;
          return prev > totalHoursHeightPx ? prev : totalHoursHeightPx;
        }, 0);

        const existingUIHeight = 140;
        const calendarHeightPx =
          hourCardHeightPx === 0 ? existingUIHeight : hourCardHeightPx + existingUIHeight;

        return {
          key: user.id,
          user: {
            id: user.id,
            name: user.name,
            friendlyName: user.name.split(' ')[0],
            avatar: user.avatar ? user.avatar.medium_retina : null,
          },
          hoursTotals,
          hoursByDay,
          ui: {
            // todo: Determine the height of each virtualized
            calendarHeightPx,
          },
          // Used to update virtualized list when needed:
          cacheSignature: {
            numHours: hours.reduce((prev, curr) => {
              return (prev += curr.day);
            }, `${user.id}-${hours.length}`),
            calendarHeightPx,
          },
        };
      });
  }
);

const getMultiUserSelectionState = createSelector(
  [getReportSelection, getMultiUsersDaysWithHours, getDayProperties],
  (selection, usersDaysWithHours, dayProperties) => {
    const active = selection.hourIds.length > 0;
    const selectionSet = new Set(selection.hourIds);
    const selectionStateByUserAndDay: Record<number, Record<string, DaySelectionState>> = {};
    usersDaysWithHours.forEach(({ user, hoursByDay }) => {
      const selectionStateByDay: Record<string, DaySelectionState> = {};
      selectionStateByUserAndDay[user.id] = selectionStateByDay;
      Object.keys(hoursByDay).forEach(date => {
        const day = hoursByDay[date];
        const isDayLocked = isDayLockedForUser(user.id, dayProperties[date]);
        const selectableHours = day.hours.filter(hour => !isDayLocked && !hour.isLocked);
        const numUnselectedHours = selectableHours.filter(
          hour => !selectionSet.has(hour.id)
        ).length;
        const hasSelectedHours = day.hours.some(h => selectionSet.has(h.id));
        selectionStateByDay[date] = {
          active,
          hasSelectedHours,
          hasUnselectedHours: numUnselectedHours > 0,
          disabled: selectableHours.length === 0,
        };
      });
    });
    const selectionStateByDayTotals = Object.values(selectionStateByUserAndDay).reduce<
      Record<string, DaySelectionState>
    >((byDayMap, userState) => {
      Object.keys(userState).forEach(date => {
        const dayState = userState[date];
        const existingDayState = byDayMap[date] || {
          active: false,
          hasSelectedHours: false,
          hasUnselectedHours: false,
          disabled: true,
        };
        byDayMap[date] = {
          active: existingDayState.active || dayState.active,
          hasSelectedHours: existingDayState.hasSelectedHours || dayState.hasSelectedHours,
          hasUnselectedHours: existingDayState.hasUnselectedHours || dayState.hasUnselectedHours,
          disabled: existingDayState.disabled && dayState.disabled,
        };
      });
      return byDayMap;
    }, {});
    return {
      byUserAndDay: selectionStateByUserAndDay,
      totalsByDay: selectionStateByDayTotals,
    };
  }
);

export type GetMultiUserCalendarProps = ReturnType<typeof getMultiUserCalendarProps>;

const getDayLockedStateForAllSelectedUsers = createSelector(
  [getDates, getFilter, getDayPropertiesForDates],
  (dates, filter, dayProperties) => {
    const lockedState: Record<
      string,
      {
        areAllLocked: boolean;
      }
    > = {};
    const { userIds } = filter;
    const selectedUsersCount = userIds.length;

    dates.forEach(date => {
      const lockedCellsCount = dayProperties[date]
        .filter(prop => userIds.includes(prop.user_id))
        .reduce((count, cell) => count + (cell.locked ? 1 : 0), 0);
      lockedState[date] = {
        areAllLocked: selectedUsersCount === lockedCellsCount,
      };
    });

    return lockedState;
  }
);

export const getMultiUserCalendarProps = createSelector(
  [
    getDates,
    getDayHeaderKeys,
    getMultiUsersDaysWithHours,
    getDayPropertiesForDates,
    getCalendarUi,
    getUserPermissions,
    getUserId,
    getMultiUserSelectionState,
    getDayLockedStateForAllSelectedUsers,
  ],
  (
    dates,
    dayHeaderKeys,
    usersDaysWithHours,
    dayProperties,
    ui,
    permissions,
    currentUserId,
    selectionState,
    lockedState
  ) => {
    const virtualizedCacheBuster = JSON.stringify(
      usersDaysWithHours.map(calendar => calendar.cacheSignature)
    );
    return {
      dates,
      dayHeaderKeys,
      usersDaysWithHours,
      dayProperties,
      virtualizedCacheBuster,
      ui,
      permissions,
      currentUserId,
      selectionState,
      lockedState,
    };
  }
);

// Todo: Move this type to teams selector
export interface GetTeamPickerProps {
  teams: Array<TeamWithUsers>;
}

export const getCalendarSplashTeamPickerProps: (iState: IState) => GetTeamPickerProps =
  createSelector([getTeamsWithUsers], teams => {
    return { teams };
  });

export type CalendarRouteUi = ReturnType<typeof getCalendarRouteUi>;

export interface GetCalendarRouteProps {
  date: string;
  span: FilterSpan;
  cost: Cost;
  estimatedCost: Cost;
  duration: Duration;
  durationRoundedDown: Duration;
  estimatedDuration: Duration;
  users: Array<object>;
  teams: Array<object>;
  XhrHoursCacheBuster: string;
  routeProperties: {
    basePath: string;
  };
  isLoading: boolean;
  ui: CalendarRouteUi;
}

export const getShowInlineMemory = createSelector(
  [getDerivedDatesInfo, getFilter],
  ({ isSingleDay }, filter) => isSingleDay && !filter.multiUserMode && filter.isActingAsSelf
);

const getCalendarRouteUi = createSelector(
  [
    getFilter,
    getCalendarUser,
    getCurrentAccount,
    getHourTimerStatus,
    getDerivedDatesInfo,
    getHasHourDraft,
    getHasReachedUserLimit,
    getIsUserInExperimentalFeatureGroup,
    getUser,
    getUserProperties,
    getUserPropertiesXhrStatus,
    getShowInlineMemory,
    getReportSelection,
    getHasEnabledTic,
    getIsViewingTimesheet,
  ],
  (
    filter,
    calendarUser,
    account,
    hourTimerStatus,
    { isSingleDay, isTodayInView },
    hasHourDraft,
    hasReachedUserLimit,
    showDebugInfo,
    user,
    userProperties,
    userPropertiesXhrStatus,
    showInlineMemory,
    batchSelection,
    hasEnabledTic,
    isViewingTimesheet
  ) => {
    const hasAccessToTeams = !!account.features.teams;
    const hasFetchedUserProperties = !!userPropertiesXhrStatus.hasInitialized;
    const { calendarUserId } = filter;

    return {
      showDebugInfo,
      activeUser: {
        name: calendarUser.name,
        friendlyName: filter.isActingAsSelf ? 'Me' : calendarUser.name.split(' ')[0],
        avatar: calendarUser.avatar ? calendarUser.avatar.medium_retina : null,
      },
      activeUserIds: filter.multiUserMode ? filter.userIds : [calendarUserId],
      actingAsUserId: calendarUserId,
      isActingAsSelf: filter.isActingAsSelf,
      currency: account.currency,
      hourTimerStatus,
      isSingleDay,
      isTodayInView,
      showInlineMemory,
      hasAccessToMultiUserMode: !!account.features.company_view,
      hasAccessToAiTimesheetGeneration: !!account.features.ai_timesheet_creation,
      hasAccessToTeams,
      multiUserMode: filter.multiUserMode,
      showUpgradeSubscriptionInUserDropdown:
        hasReachedUserLimit && !account.features.log_hours_for_others,
      isForecastFeatureEnabled: !!account.features.tasks,
      isDayLockingFeatureEnabled: !!account.features.day_locking,
      preferTaskDropdown: !!userProperties[HIDE_TASK_SIDEBAR],
      hideHourlyRates: !!user.hide_hourly_rate,
      hasHourDraft,
      hasBatchSelection: batchSelection.hourIds.length > 0,
      hasFetchedUserProperties,
      hasEnabledTic,
      showTimesheetProposal: isViewingTimesheet,
    };
  }
);

export const getCalendarContextView = createSelector(
  [getFilter],
  (filter): HourAnalyticsSaveContext['calendar_view'] => {
    return filter.multiUserMode
      ? 'Company Week'
      : filter.span === 'day'
        ? 'Day'
        : filter.span === 'week'
          ? 'Week'
          : filter.span === 'month'
            ? 'Month'
            : undefined;
  }
);

const getCalendarBatchSelection = createSelector(
  [
    getReportSelection,
    getHours,
    getHasHourStatesFeature,
    getUserSelectableHourStates,
    getHasBillingFeature,
  ],
  (selection, hours, hasHourStatesFeature, userSelectableStates, hasBillingFeature) => {
    const states = Object.values(userSelectableStates).map(state => ({
      state,
      hasSelectableHours: hours.some(hour => (hour.state?.id ?? 0) === state.id),
    }));
    const selectedHoursBillable = hours
      .filter(hour => selection.hourIds.includes(hour.id))
      .every(hour => hour.billable);

    return {
      active: selection.hourIds.length > 0,
      allSelected: hours.length > 0 && selection.hourIds.length === hours.length,
      hasHourStatesFeature,
      hasBillingFeature,
      selectedHoursBillable,
      hourStates: states,
      numSelectableHours: hours.length,
    };
  }
);

export const getCalendarHeaderProps = createSelector(
  [
    getCurrentAccount,
    getFilter,
    getCostDurationOfHoursExceptTimer,
    getHourTimerStatus,
    getDerivedDatesInfo,
    getUser,
    getHasReachedUserLimit,
    getNonDeletedUsers,
    getTeamsAsDropdownOptions,
    getTasksMeta,
    getHasConnectedMemorySource,
    getCalendarBatchSelection,
    getUserPermissions,
    getDayPropertiesForDates,
    getStartOfWeek,
  ],
  (
    account,
    filter,
    { cost, durationRoundedDown, estimatedDuration, estimatedCost, mixedCurrencies },
    hourTimerStatus,
    { since, upto, isTodayInView, isSingleDay },
    user,
    hasReachedUserLimit,
    users,
    teams,
    { canSeeTasks, accountIsTrialingTasks },
    hasConnectedMemorySource,
    batchSelection,
    permissions,
    dayProperties,
    startOfWeek
  ) => {
    const alwaysHideTasksSidebar = !account.features.tasks && !accountIsTrialingTasks;
    return {
      account,
      alwaysHideTasksSidebar,
      canSeeTasks,
      filter,
      hourTimerStatus,
      cost,
      durationRoundedDown,
      estimatedDuration,
      estimatedCost,
      since,
      upto,
      isTodayInView,
      isSingleDay,
      user,
      showUpgradeSubscriptionInUserDropdown:
        hasReachedUserLimit && !account.features.log_hours_for_others,
      activeUserIds: filter.multiUserMode ? filter.userIds : [filter.calendarUserId],
      users,
      teams: account.features.teams ? teams : EMPTY_ARRAY,
      hasConnectedMemorySource,
      hasMultipleCurrencies: mixedCurrencies,
      batchSelection,
      permissions,
      dayProperties,
      startOfWeek,
    };
  }
);

export const getCalendarRoute = createSelector(
  [
    getNonDeletedUsers,
    getTeamsAsDropdownOptions,
    getCalendarUserId,
    getFilter,
    getDates,
    getCurrentAccount,
    getCostDurationOfHoursExceptTimer,
    getCalendarRouteUi,
    getHoursXhrStatus,
  ],
  (
    users,
    teams,
    calendarUserId,
    filter,
    dates,
    account,
    sums,
    ui,
    xhrStatus
  ): GetCalendarRouteProps => {
    const { cost, duration, durationRoundedDown, estimatedDuration, estimatedCost } = sums;
    const since = dates[0];
    const upto = dates[dates.length - 1];
    const hasAccessToTeams = !!account.features.teams;
    const xhrUIState = xhrStatusUIState(xhrStatus, 'hours');
    const result: GetCalendarRouteProps = {
      // Intended to tell the connected route when to fetch data
      // This should NOT re-fetch data if we go from ie, week -> day and
      // day was part of the week to avoid re-fetching identical data.
      date: filter.date,
      span: filter.span,
      cost,
      estimatedCost,
      duration,
      durationRoundedDown,
      estimatedDuration,
      // Feels kind of heavy to pass users on from here, should we consider
      // connecting the header? What about the initial "select a team/user" thing?
      users,
      teams: hasAccessToTeams ? teams : EMPTY_ARRAY,
      // todo: Be more specific here, so that changing the data to something that's already
      // within the current range doesn't trigger re-fetching of hours.json endpoint.

      XhrHoursCacheBuster: JSON.stringify({
        since,
        upto,
        multiUserMode: filter.multiUserMode,
        // calendarUserId changes when draft opens in multiUserMode, we want to avoid excessive XHR's
        userIdRef: filter.multiUserMode ? filter.userIds.length : calendarUserId,
      }),
      // todo XhrTimelineCacheBuster
      routeProperties: {
        basePath: `/${account.id}/calendar/${filter.span}`,
      },
      isLoading: xhrUIState.isLoading,
      ui,
    };
    return result;
  },
  { memoize: lruMemoize, memoizeOptions: { equalityCheck: isEqual } }
);

export interface GetCopyOrMoveHourModalProps {
  date: string;
  userId: number;
  hours: Array<HourAsCard>;
  startOfWeek: number;
  selectedHourIds: Array<number>;
  costAndDurationSummary: SummarizeCostAndDurationOutput;
  mode: BatchHourModalMode;
  dayProperties: Record<string, Array<DayProperties>>;
  isDayLockingFeatureEnabled: boolean;
}

export const getCopyOrMoveHourModalProps = createSelector(
  [
    getCurrentAccount,
    getFilter,
    getSelection,
    getHoursAsCards,
    readBatchHourModalMode,
    getDayProperties,
  ],
  (account, filter, selection, hoursAsCards, mode, dayProperties): GetCopyOrMoveHourModalProps => {
    const hours = hoursAsCards.filter(({ id }) => selection.hourIds.includes(id));
    const date = hours.length ? hours[0].day : filter.date;
    return {
      date,
      userId: filter.calendarUserId,
      selectedHourIds: selection.hourIds,
      hours,
      startOfWeek: account.start_of_week,
      costAndDurationSummary: summarizeCostAndDuration(hours),
      mode,
      dayProperties,
      isDayLockingFeatureEnabled: !!account.features.day_locking,
    };
  }
);
export { getDerivedDatesInfo };
